// Auth gate for the CRM SPA. The session is an HttpOnly cookie set by the API
// (api/auth/sso.py, api/auth/google.py), so there is no token handling in JS.
//
// Two sign-in surfaces, both YLLW-branded:
//   - Public "/"     → "Sign in with Microsoft" (YLLW users, Entra).
//   - Hidden "/admin/login" → "Sign in with Google" (Solvigo staff, @solvigo.ai only).
//     Unlinked from the UI; the real gate is the verified-domain check server-side.
//
// Flow: probe /api/me — success renders the app, 401 shows the sign-in screen,
// 403 shows access-denied. In local dev the API authenticates via DEV_USER_EMAIL,
// so the gate falls straight through; when a provider isn't configured in this
// environment the screen falls back to the dev sign-in.
import { useEffect, useState, type ReactNode } from 'react'
import { ApiError, useAuthConfig } from '@/shared/api/core'
import { useMe } from '@/shared/api/session'
import { BootSkeleton } from '@/shared/Skeletons'
import { BrandLoader } from '@/shared/white-label/BrandLoader'
import { useActiveBrand } from '@/shared/white-label/useBrand'
import { clearStored, readStored, writeStored } from '@/shared/lib/storage'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'

// Whether this browser has ever completed a sign-in. Purely a rendering hint —
// it decides which placeholder to show while /api/me is in flight, never
// whether anything is authorized (the HttpOnly session cookie does that, and
// the API re-checks on every request). Without it a signed-out visitor would
// stare at a fake app shell for the length of a cold start before being
// bounced to the sign-in card.
const RETURNING_KEY = 'auth.returning'

function isReturning(): boolean {
  return readStored(RETURNING_KEY, { legacyKey: 'crm.returning' }) === '1'
}
function setReturning(v: boolean) {
  try {
    if (v) writeStored(RETURNING_KEY, '1')
    else clearStored(RETURNING_KEY)
  } catch { /* private mode / quota */ }
}

// True when the browser is on the hidden Solvigo-staff entry (/admin/login).
function onStaffRoute(): boolean {
  return window.location.pathname.replace(/\/+$/, '').toLowerCase().endsWith('/admin/login')
}

// Placeholder for visitors we have no sign-in history for. Stays a branded
// hold rather than an app silhouette we might have to take away.
function Splash() {
  const brand = useActiveBrand()
  return (
    <div className="app" data-surface="fog" data-brand={brand.key}>
      <BrandLoader className="h-screen" />
    </div>
  )
}

// Centered, logo-only card on the app's fog background. Deliberately says
// nothing about the product or the account — it's a public page, so it carries
// no copy that would describe what this is or hint at a staff entrance.
function SignInCard({ children }: { children: ReactNode }) {
  return (
    <div data-surface="fog" className="flex h-screen items-center justify-center px-4">
      <Card className="w-[min(360px,100%)] rounded-2xl text-center shadow-[0_24px_60px_-24px_rgba(10,10,10,0.25)]">
        <CardContent>
          <img src="/brand/logo.svg" alt="YLLW" className="mx-auto h-7 w-auto" />
          <div className="mt-8">{children}</div>
        </CardContent>
      </Card>
    </div>
  )
}

function MicrosoftButton() {
  const returnTo = window.location.origin + window.location.pathname
  const href = `/auth/sso/start?returnTo=${encodeURIComponent(returnTo)}`
  return (
    <Button asChild variant="yellow" size="lg" className="w-full">
      <a href={href}>Logga in med Microsoft</a>
    </Button>
  )
}

function GoogleG() {
  return (
    <svg viewBox="0 0 18 18" width="16" height="16" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z" />
      <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3-2.33z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.46 3.44 1.35l2.58-2.58A9 9 0 0 0 .96 4.95l3 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
    </svg>
  )
}

function GoogleButton() {
  // Land back in the app after sign-in (not on /admin/login).
  const href = `/auth/google/start?returnTo=${encodeURIComponent(window.location.origin + '/')}`
  return (
    <Button asChild variant="outline" size="lg" className="w-full">
      <a href={href}>
        <GoogleG />
        Logga in med Google
      </a>
    </Button>
  )
}

// Dev-only: when no provider is wired in this environment, sign in as a known
// email via the `dev:<email>` bypass (the API rejects `dev:` tokens in prod).
function DevLogin() {
  const [email, setEmail] = useState('')
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        const v = email.trim()
        if (!v) return
        window.location.href = `/auth/dev-login?email=${encodeURIComponent(v)}&returnTo=/`
      }}
    >
      <Input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@solvigo.ai"
        className="mb-3"
      />
      <Button type="submit" variant="yellow" size="lg" className="w-full">
        Fortsätt
      </Button>
    </form>
  )
}

function LoginScreen() {
  const cfg = useAuthConfig()
  // Hold the card, not a blank screen: /auth/config only decides which button
  // goes inside, so the frame can be on screen already when it lands.
  if (cfg.isLoading) {
    return (
      <SignInCard>
        <div role="status" aria-label="Laddar inloggning">
          <Skeleton className="h-[42px] w-full rounded-lg" />
        </div>
      </SignInCard>
    )
  }

  if (onStaffRoute()) {
    // Solvigo staff entry — Google when configured, dev fallback otherwise.
    // Visually identical to the public screen so it doesn't announce itself.
    return <SignInCard>{cfg.data?.google ? <GoogleButton /> : <DevLogin />}</SignInCard>
  }
  // Public YLLW entry — Microsoft when configured, dev fallback otherwise.
  return <SignInCard>{cfg.data?.sso ? <MicrosoftButton /> : <DevLogin />}</SignInCard>
}

function AccessDenied() {
  return (
    <SignInCard>
      <p className="text-sm text-ink-700">
        Ditt konto har inte åtkomst till det här CRM:et. Kontakta en administratör.
      </p>
    </SignInCard>
  )
}

export function AuthGate({ children }: { children: ReactNode }) {
  const me = useMe()
  const status = (me.error as ApiError)?.status

  // Remember a completed sign-in, and forget it on a 401 so an expired session
  // doesn't keep showing the app shell to someone who now has to sign in.
  useEffect(() => {
    if (me.isSuccess) setReturning(true)
    else if (me.isError && status === 401) setReturning(false)
  }, [me.isSuccess, me.isError, status])

  if (me.isLoading) return isReturning() ? <BootSkeleton /> : <Splash />
  if (me.isError) {
    return status === 403 ? <AccessDenied /> : <LoginScreen />
  }
  return <>{children}</>
}
